import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce, isEmpty } from "lodash";
import { MDBIcon, MDBAnimation, MDBProgress } from "mdbreact";
import { useToasts } from "react-toast-notifications";
import { BROWSE as MENUS } from "../../../services/redux/slices/commerce/catalog/menus";
import { globalSearch } from "../../../services/utilities";
import Notification from "./notifications";
import "../style.css";
import Swal from "sweetalert2";

/**
 * A Search component that allows the user to search for a menu item by name.
 * The component will make an API call to search for menu items and render a list of results below the search input.
 * The user can select a menu item from the list and the setMenu callback will be called with the selected menu item.
 * The component also renders a button to register a new menu item if no menu item record is found with the search key.
 * The setRegister callback will be called with the search key when the button is clicked.
 *
 * @param {function} setMenu - A callback function that will be called when a menu item is selected from the list.
 * @param {function} setRegister - A callback function that will be called when the button to register a new menu item is clicked.
 *
 * @returns {JSX.Element} user
 */
export default function Search({
  setMenu,
  setRegister = () => {},
  filtered = [],
  customer = {},
  strict = false,
}) {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    { collections } = useSelector(({ menus }) => menus),
    [match, setMatch] = useState([]),
    [searchKey, setSearchKey] = useState(""),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts(),
    dispatch = useDispatch();

  const inputRef = useRef(null); // Reference to the input field

  // initial values
  useEffect(() => {
    if (token && activePlatform.branchId) {
      const branchId = activePlatform.branchId;
      if (collections.length > 0) return;

      dispatch(MENUS({ token, key: { branchId } })).catch((error) => {
        console.error("Error fetching menus:", error);
      });
    }
  }, [token, dispatch, activePlatform, collections.length]);

  // Debounced search function to avoid too many re-renders
  const debouncedSearch = useMemo(
    () =>
      debounce((key) => {
        setIsLoading(false);

        if (key.trim().length <= 1) return setMatch([]);
        const _match = globalSearch(
          isEmpty(filtered) ? collections : filtered,
          key.trim()
        );
        setMatch(_match);
      }, 500),
    [collections, filtered, setMatch, setIsLoading]
  ); // dependencies

  const handleChange = (value) => {
    if (strict && !customer._id) {
      Swal.fire({
        title: "Patient Required",
        html: `
    <div style="text-align: left; font-size: 15px; line-height: 1.6;">
      <p>You need to <b>select</b> or <b>register</b> a patient before you can charge menus.</p>
      <hr/>
      
      <p style="margin-top: 10px;"><b>🔍 To select a patient:</b></p>
      <ol style="padding-left: 20px; margin: 0; margin-top:-15px">
        <li>Use the <b>Search</b> bar to find an existing patient.</li>
        <li>Click on the patient from the search results to select them.</li>
      </ol>
      
      <p style="margin-top: 15px;"><b>📝 To register a new patient:</b></p>
      <ol style="padding-left: 20px; margin: 0; margin-top:-15px ">
        <li>Click the <b>Patient</b> button on the left side of first form.</li>
        <li>Fill out all the required information in the registration form.</li>
        <li>Scroll down and click the <b>Register</b> button to save the patient.</li>
      </ol>

      <hr style="margin: 15px 0;"/>
      <p style="font-weight: 500; text-align: center;">
        ✅ After completing these steps, you can now type and charge menus.
      </p>
    </div>
  `,
        confirmButtonText: "Got it",
        confirmButtonColor: "#1266f1",
        width: 600,
      });
      return;
    }
    setSearchKey(value);
    setIsLoading(true);
    // Debounced search trigger
    debouncedSearch(value);
  };

  const handleSelect = (menu) => {
    setMenu(menu);
    setSearchKey(""); // Reset the search key after selection
    setMatch([]); // Clear the match list after selection
    inputRef.current.focus(); // Keep focus on the input field after selection
  };

  const handleRegister = () => {
    setRegister(searchKey);
    setSearchKey(""); // Reset the search key after registration
    setMatch([]); // Clear the match list after registration
    inputRef.current.focus(); // Keep focus on the input field after registration
  };

  const handleClear = () => {
    setSearchKey(""); // Clear the search key
    setMatch([]); // Clear the match list
  };

  return (
    <div className="d-flex align-items-center " style={{ width: "100%" }}>
      <Notification didSearch={match.length > 0} />
      <div className={`searchable-search  ${searchKey && "active"}`}>
        <div className="searchable-search-suggestions">
          {!isLoading ? (
            <>
              {searchKey && match.length === 0 && (
                <li onClick={handleRegister} className="text-dark">
                  No services found for "{searchKey}". try another keywords
                </li>
              )}
              {match.length === 0 && !searchKey && (
                <li>Please type a menu name.</li>
              )}
              {match.length > 0 &&
                match.map((menu, index) => {
                  const { description = "", abbreviation = "", opd = 0 } = menu;

                  return (
                    <li
                      style={{ listStyle: "none" }}
                      key={`menu-suggestion-${index}`}
                      onClick={() => {
                        if (opd) {
                          handleSelect(menu);
                        } else {
                          addToast("This product has no set price.", {
                            appearance: "warning",
                          });
                        }
                      }}
                    >
                      <div className="d-flex align-items-left justify-content-between menu-suggestion p-2">
                        <span className="text-left  ">
                          {description && (
                            <span className="description text-dark">
                              {description.toLowerCase().toUpperCase()}
                            </span>
                          )}
                        </span>
                        <span className="ml-3 text-dark">{abbreviation}</span>
                      </div>
                    </li>
                  );
                })}
            </>
          ) : (
            <>
              {new Array(5).fill("").map((_, index) => (
                <MDBAnimation
                  key={index}
                  className="p-1 ml-2 mr-2 "
                  type="flash"
                  infinite
                  delay={`${index + 1}00ms`}
                  duration="3000ms"
                >
                  <MDBProgress
                    color="light"
                    value={3000}
                    id="progress-table"
                  ></MDBProgress>
                </MDBAnimation>
              ))}
            </>
          )}
        </div>
        <input
          ref={inputRef} // Attach the reference to the input element
          placeholder="Search menus..."
          value={searchKey}
          onChange={({ target }) => handleChange(target.value)}
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="submit"
          className={`${match.length > 0 && "bg-danger"}`}
          onClick={handleClear}
        >
          <MDBIcon
            icon={match.length > 0 ? "times" : "search"}
            className="search-icon"
          />
        </button>
      </div>
    </div>
  );
}
